import { useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { DateTime } from 'luxon';
import {
	Functor,
	any,
	complement,
	equals,
	filter,
	find,
	includes,
	keys,
	map,
	or,
	propEq,
	reject,
	values,
} from 'ramda';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FolxLoader from '@ui/atoms/FolxLoader';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';
import SelectableButton, { ISelectableButtonProps } from '@ui/atoms/SelectableButton';
import SelectableButtonGroup from '@ui/atoms/SelectableButtonGroup';

import Checkbox from '@ui/forms/Checkbox';
import PhoneTextInput from '@ui/forms/PhoneTextInput';
import TextInput from '@ui/forms/TextInput';

import NewInputWrapper from '@ui/molecules/NewInputWrapper';
import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import DateBox from '@ui/organisms/DateBox';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import useFlowData from '@hooks/useFlowData';
import { IAuthenticatedMember, IMultiFactorConfiguration, ISmsNumber } from '@hooks/useMemberInfo';
import { useTracking } from '@hooks/useTracking';

import { commonErrors } from '@utils/error';
import { doesNotExist, exists } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';
import { checkbox, optionalString } from '@utils/validation';

import FlowContext from '@providers/FlowContextProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import pronounOptions from '@data/pronouns';

import { IFlowStepProps } from '@folx-types/flow-steps';

import { multiFactor, multiFactorName } from '@graphql/fragments/multiFactorConfiguration';
import { queryName, smsNumber, smsNumberName } from '@graphql/fragments/smsNumber';
import CURRENT_MEMBER_WITH_NAME_QUERY from '@graphql/queries/currentMemberWithName.gql';

interface IPersonalInfoStepProps extends IFlowStepProps {}

interface IPersonalInfoStepContent {
	pageContent: {
		title: string;
		subtitle: string;
		dob_label: string;
		legal_sex_label: string;
		legal_sex_tooltip_title: string;
		legal_sex_tooltip_description: string;
		stats: {
			f: string;
			m: string;
			x: string;
		};
		pronouns_label: string;
		pronouns_tooltip_title: string;
		pronouns_tooltip_description: string;
		other_pronouns: string;
		phone_number_label: string;
		phone_number_placeholder: string;
		notification_fineprint: string;
	};
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);
const FullWidthForm = fullWidth('form');
const FullWidthSelectableButton = fullWidth<ISelectableButtonProps>(SelectableButton);
const FullWidthButton = fullWidth<IButtonProps>(Button);
const FullWidthSubmitButton = fullWidth<ISubmitButtonProps>(SubmitButton);

const PersonalInfoStep: FC<IPersonalInfoStepProps> = ({ stepKey }) => {
	const [submitting, setSubmitting] = useState(false);
	const [arePronounsSelected, setArePronounsSelected] = useState(false);
	const [smsMfaEnrolled, setSmsMfaEnrolled] = useState(false);
	const { data: nameData, loading: nameLoading } = useQuery<{
		currentMember: {
			id: string;
			affirmingFirstName: string;
			affirmingLastName: string;
			legalFirstName: string;
			legalLastName: string;
		};
	}>(CURRENT_MEMBER_WITH_NAME_QUERY);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const { loading: memberLoading, data: memberData } = useQuery<
		IAuthenticatedMember<IMultiFactorConfiguration & ISmsNumber>
	>(combineMemberFragments([multiFactorName, smsNumberName], [multiFactor, smsNumber], queryName), {
		client: graphQLv2Client,
	});

	useEffect(() => {
		if (nameLoading) return;
		if (!nameData?.currentMember?.legalFirstName || !nameData.currentMember?.legalLastName) {
			navigate('/app/member');
			return;
		}
		setValue('preferredFirstName', nameData.currentMember?.affirmingFirstName);
		setValue('preferredLastName', nameData.currentMember?.affirmingLastName);
		setValue('legalFirstName', nameData.currentMember?.legalFirstName);
		setValue('legalLastName', nameData.currentMember?.legalLastName);
	}, [nameData]);

	useDeepCompareEffect(() => {
		if (!memberLoading && memberData && memberData?.authenticatedMember) {
			if (memberData?.authenticatedMember?.multiFactorConfiguration?.smsEnrolled) {
				setSmsMfaEnrolled(true);
				setValue('phone', memberData?.authenticatedMember?.smsNumber);
				setValue('notify', memberData?.authenticatedMember?.smsContactConsent);
			}
		}
	}, [memberData]);

	const { pageContent } = useStaticQuery<IPersonalInfoStepContent>(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "personal-info" }) {
					title
					subtitle
					dob_label
					legal_sex_label
					legal_sex_tooltip_title
					legal_sex_tooltip_description
					stats {
						f
						m
						x
					}
					pronouns_label
					pronouns_tooltip_title
					pronouns_tooltip_description
					other_pronouns
					phone_number_label
					phone_number_placeholder
					notification_fineprint
				}
			}
		`
	);
	const {
		title,
		subtitle,
		dob_label,
		legal_sex_label,
		legal_sex_tooltip_title,
		legal_sex_tooltip_description,
		stats,
		pronouns_label,
		pronouns_tooltip_title,
		pronouns_tooltip_description,
		other_pronouns,
		phone_number_label,
		phone_number_placeholder,
		notification_fineprint,
	} = pageContent;

	useTracking('view-onboarding-step', {
		step_name: 'SHARED_INTAKE',
		flow_id: 'hrt_e',
	});

	const pronounMessage = 'Enter at least one pronoun';

	const PRONOUNS = 'pronouns';
	const OTHER = 'other';
	const HAS_OTHER_PRONOUNS = 'hasOtherPronouns';

	const schema = z.object({
		dob: z
			.string({
				invalid_type_error: 'Enter a valid birth date',
			})
			.refine((date) => {
				const dateTime = DateTime.fromISO(date);
				return dateTime.isValid && dateTime.year >= 1900 && dateTime.year <= DateTime.now().year;
			}, 'Enter a valid birth date'),
		legalSex: z.enum(['M', 'F', 'X']),
		phone: z.string().min(1),
		notify: checkbox,
		[PRONOUNS]: z
			.any()
			.transform((values: Record<string, unknown>) => {
				if (doesNotExist(values)) {
					return;
				}

				const otherPronouns = exists(values[OTHER]) ? [values[OTHER]] : [];
				const boolPronouns = reject((pronoun: string) => equals(OTHER)(pronoun))(keys(values));
				const filteredPronouns = filter((pronoun: string) => {
					const isNotNumber = equals(NaN);
					return values[pronoun] && isNotNumber(Number(pronoun));
				}, boolPronouns);
				return [...otherPronouns, ...filteredPronouns];
			})
			.refine((pronouns: string[]) => pronouns?.length ?? 0 > 0, pronounMessage),
		[HAS_OTHER_PRONOUNS]: checkbox,
		preferredFirstName: optionalString,
		preferredLastName: optionalString,
		legalFirstName: optionalString,
		legalLastName: optionalString,
	});

	type Schema = Partial<z.input<typeof schema>>;

	const context = useContext(FlowContext);
	const { handleFormSubmission, handleNavBack, existingFormData, flowId } = context;
	const { submitFormData } = useFlowData(flowId);

	const { handleSubmit, register, control, formState, watch, resetField, setValue } =
		useForm<Schema>({
			defaultValues: existingFormData,
			resolver: zodResolver(schema),
			mode: 'onChange',
		});

	const { fields, append, remove } = useFieldArray({
		name: PRONOUNS,
		control: control,
		shouldUnregister: true,
	});

	const otherChecked = watch(HAS_OTHER_PRONOUNS);
	const otherPronouns = watch(`${PRONOUNS}.${OTHER}`);
	const checkedPronouns = watch(PRONOUNS);

	const onSubmit = async (data) => {
		setSubmitting(true);

		handleFormSubmission(data, stepKey).finally(() => {
			setSubmitting(false);
		});
	};

	const { errors, isSubmitted } = formState;

	const updatePronounState = () => {
		const anyPronounsChecked = any((v: boolean | string) =>
			or(equals<boolean | string>('true')(v), equals<boolean | string>(true)(v))
		);
		const pronounValues = values(checkedPronouns);
		const pronounsSelected = anyPronounsChecked(pronounValues) || exists(otherPronouns);
		setArePronounsSelected(pronounsSelected);
	};

	useDeepCompareEffect(() => {
		updatePronounState();
	}, [{ ...checkedPronouns }, otherPronouns]);

	useEffect(() => {
		remove();
	}, []);

	useEffect(() => {
		if (!otherChecked) {
			setValue(`${PRONOUNS}.${OTHER}`, '');
		}
	}, [otherChecked]);

	useEffect(() => {
		if (doesNotExist(existingFormData)) {
			return;
		}
		const pronounValues = existingFormData?.pronouns ?? [];

		map<string, Functor<unknown>>((pronoun) => {
			const cannotFind = complement(find<Record<string, string>>(propEq('name', pronoun)));
			const value = includes(pronoun, pronounValues);
			return cannotFind(fields) ? append({ value, name: pronoun }) : null;
		}, pronounOptions);

		const otherPronouns = find((pronoun: string) => {
			const doesNotInclude = complement(includes(pronoun));
			return doesNotInclude(pronounOptions);
		}, pronounValues);

		updatePronounState();

		if (exists(otherPronouns)) {
			resetField(`${PRONOUNS}.${OTHER}`, { defaultValue: otherPronouns });
		}
	}, [pronounOptions, existingFormData]);

	useEffect(() => {
		if (!existingFormData.stripeIdempotencyKey) {
			existingFormData.stripeIdempotencyKey = uuidv4();
			submitFormData({ stripeIdempotencyKey: existingFormData.stripeIdempotencyKey });
		}
	}, []);

	if (memberLoading) return <FolxLoader />;

	return (
		<>
			<SpacedColumn gap="sm" marginBottom="lg">
				<HeadingText>{title}</HeadingText>
				<BasicText variant="fineprint">{subtitle}</BasicText>
			</SpacedColumn>
			<FullWidthForm onSubmit={handleSubmit(onSubmit)}>
				<SpacedColumn marginBottom="xxl" gap="lgxl">
					<DateBox
						label={dob_label}
						error={!!errors?.dob}
						errorLabel={errors?.dob?.message ?? ''}
						name="dob"
						registerFunc={register}
						setValueFunc={setValue}
						value={existingFormData?.dob}
					/>
					<FullWidthColumn gap="sm">
						<NewInputWrapper
							title={legal_sex_label}
							titleType="field"
							tooltipTitle={legal_sex_tooltip_title}
							tooltipDescription={legal_sex_tooltip_description}
							error={isSubmitted && !!errors?.legalSex && commonErrors.selectionRequired}>
							<Controller
								name="legalSex"
								control={control}
								render={(args) => {
									const field = {
										...args.field,
										value: args.field.value ?? '',
									};
									return (
										<SelectableButtonGroup layout="row" name="legalSex">
											<FullWidthSelectableButton optionValue="F" {...field}>
												{stats.f}
											</FullWidthSelectableButton>
											<FullWidthSelectableButton optionValue="M" {...field}>
												{stats.m}
											</FullWidthSelectableButton>
											<FullWidthSelectableButton optionValue="X" {...field}>
												{stats.x}
											</FullWidthSelectableButton>
										</SelectableButtonGroup>
									);
								}}
							/>
						</NewInputWrapper>
					</FullWidthColumn>
					<FullWidthColumn gap="sm">
						<NewInputWrapper
							title={pronouns_label}
							titleType="field"
							tooltipTitle={pronouns_tooltip_title}
							tooltipDescription={pronouns_tooltip_description}
							error={
								isSubmitted && !(arePronounsSelected || otherChecked) && errors?.pronouns?.message
							}>
							{exists(fields) &&
								fields.map((field: { id: string; name?: string; value?: boolean }) => (
									<Checkbox
										{...register(`${PRONOUNS}.${field?.name ?? ''}`, {
											value: field?.value,
										})}
										key={field?.id}
										label={field?.name ?? ''}
									/>
								))}
							<Checkbox {...register(HAS_OTHER_PRONOUNS)} label={other_pronouns} />
							{otherChecked && (
								<TextInput
									error={isSubmitted && !arePronounsSelected}
									errorLabel={!arePronounsSelected && errors?.pronouns?.message}
									{...register(`${PRONOUNS}.${OTHER}`)}
								/>
							)}
						</NewInputWrapper>
					</FullWidthColumn>
					{!existingFormData.smsMfaEnrolled && !smsMfaEnrolled && (
						<FullWidthColumn gap="sm">
							<Controller
								name="phone"
								control={control}
								render={({ field }) => (
									<PhoneTextInput
										placeholder={phone_number_placeholder}
										label={phone_number_label}
										error={isSubmitted && !!errors.phone}
										errorLabel={commonErrors.inputRequired}
										type="tel"
										{...field}
									/>
								)}
							/>
							<Checkbox label={notification_fineprint} textSize="xxs" {...register('notify')} />
						</FullWidthColumn>
					)}
				</SpacedColumn>
				<input type="hidden" {...register('preferredFirstName')} />
				<input type="hidden" {...register('preferredLastName')} />
				<input type="hidden" {...register('legalFirstName')} />
				<input type="hidden" {...register('legalLastName')} />
				<NavigationSection>
					<FullWidthButton
						label="Back"
						type="button"
						dataCy="Back"
						onClick={() => handleNavBack(stepKey)}
					/>
					<FullWidthSubmitButton
						label="Next"
						variant="secondary"
						submitting={submitting}
						dataCy="Next"
					/>
				</NavigationSection>
			</FullWidthForm>
		</>
	);
};

export default PersonalInfoStep;
